<script setup>
import { ref, toRaw, computed } from 'vue';
import { useQuery } from 'villus';
import { useStore } from '@/store/Store.js';
import Wave from '@/icons/wave.svg';
import SentierDecouvertesLarge from '@/icons/sentier-decouvertes-large.svg';
import FeaturedGrid from '@/components/featured-grid/FeaturedGrid.vue';

const props = defineProps({
    data: {
        type: Object,
        default: () => {},
    },
    color: {
        type: String,
        default: '',
    },
    themeColor: {
        type: String,
        default: '',
    },
});

const store = useStore();
const variables = { lang: store.lang.value };

let posts = props.data.data.selectedPosts;

if (!props.data.data || !props.data.data.selectedPosts || !props.data.data.selectedPosts.length || props.data.data.selectedPosts.length == 0) {
    const { data: queryData } = await useQuery({
        variables,
        query: `
        query FetchSentier($lang: [String]) {
            selectedPosts: entries(site: $lang, section: "product", limit: 6) {
                typeHandle
                id,
                title,
                uri,
                ... on product_Entry {
                    color,
                    ingredients: productIngredients { id, title },
                    cover: flexibleImage { 
                        url @transform(format: "webp", quality: 80, width: 800, height: 426, immediately: true), 
                        title,
                        height,
                        width
                    }
                }
            }
        }
    `,
    });

    posts = queryData.value.selectedPosts;
}

const attrs = computed(() => ({
    posts,
    'cta-target': '',
    'title-top': props.data.data.featuredContentTitle,
    'cta-link': props.data.data.link?.[0]?.url ?? null,
    cta: props.data.data.link?.[0]?.text ?? null,
}));
</script>

<template>
    <div class="HomeProducts Component relative my-0 justify-between bg-light-green pb-5 text-dark-green md:flex-row md:pt-5 md:pb-10 lg:pb-30">
        <div class="absolute -top-10 w-full md:-top-20">
            <Wave class="hidden fill-current text-light-green md:block 3xl:w-full" />
        </div>
        <div class="absolute left-[3%] top-[2.3%] z-10 w-[40%] translate-y-[-93%] md:top-[4%] md:w-[508px] md:max-w-[35%] lg:top-[3%] xl:max-w-[20%]">
            <SentierDecouvertesLarge />
        </div>
        <div class="absolute left-[6%] top-0 z-0 hidden h-full md:block">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="182"
                height="1666"
                viewBox="0 0 182 1666"
                fill="none"
            >
                <path
                    d="M165 0V468.704C165 541.711 138.734 612.281 91 667.522V667.522C43.2662 722.763 17 793.334 17 866.341V1127.46C17 1181.61 36.4822 1233.95 71.8874 1274.93V1274.93C107.293 1315.9 126.775 1368.25 126.775 1422.4V1666"
                    stroke="#66983F"
                    stroke-opacity="0.5"
                    stroke-width="33"
                />
            </svg>
        </div>
        <div class="absolute bottom-10 left-[3%] z-20 hidden w-[335px] max-w-[30%] md:block lg:max-w-[15%]">
            <svg
                width="335"
                height="140"
                viewBox="0 0 335 140"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M335 150.874H263.537C263.537 128.171 279.533 109.781 299.282 109.781C319.003 109.814 335 128.203 335 150.874Z"
                    fill="#66983F"
                    fill-opacity="0.5"
                />
                <path
                    d="M312.401 112.668C312.401 112.668 296.63 136.279 293.217 150.874"
                    stroke="#BCD75A"
                    stroke-width="0.878353"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M284.104 113.673C284.104 113.673 287.546 127.781 300.383 133.198"
                    stroke="#BCD75A"
                    stroke-width="0.878353"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M295.756 143.155C295.756 143.155 316.549 142.506 330.458 130.02"
                    stroke="#BCD75A"
                    stroke-width="0.878353"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M187.785 170.691C187.785 170.691 165.864 46.0823 261.308 1C261.308 1 292.032 109.23 187.785 170.691Z"
                    fill="#66983F"
                    fill-opacity="0.5"
                />
                <path
                    d="M261.308 1C261.308 1 199.211 99.6621 189.111 172.475"
                    stroke="#BCD75A"
                    stroke-width="0.849754"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M202.879 167.448C202.879 167.448 220.23 41.8988 323.348 38.0068C323.348 38.0393 319.229 151.75 202.879 167.448Z"
                    fill="#66983F"
                    fill-opacity="0.5"
                />
                <path
                    d="M202.879 167.448C202.879 167.448 278.433 62.1048 323.348 38.0068"
                    stroke="#BCD75A"
                    stroke-width="0.849754"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M272.988 81.4023L273.834 50.0068"
                    stroke="#BCD75A"
                    stroke-width="0.887682"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M243.562 115.004L245.593 72.8408"
                    stroke="#BCD75A"
                    stroke-width="0.881901"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M282.439 71.543L317.677 72.7106"
                    stroke="#BCD75A"
                    stroke-width="0.883618"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M253.916 102.744L303.937 104.139"
                    stroke="#BCD75A"
                    stroke-width="0.88751"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M225.562 137.415L267.317 144.129"
                    stroke="#BCD75A"
                    stroke-width="0.878353"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M181.86 197.999C181.86 197.999 214.333 106.862 140.021 41.541C140.021 41.541 100.551 128.754 181.86 197.999Z"
                    fill="#66983F"
                    fill-opacity="0.5"
                />
                <path
                    d="M162.224 183.859C162.224 183.859 144.45 55.2606 38.8209 51.3037C38.8209 51.3037 43.0246 167.804 162.224 183.859Z"
                    fill="#66983F"
                    fill-opacity="0.5"
                />
                <path
                    d="M162.224 183.859C162.224 183.859 84.8361 75.9855 38.8209 51.3037"
                    stroke="#BCD75A"
                    stroke-width="0.870359"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M80.0682 85.1968L79.1653 59.9961"
                    stroke="#BCD75A"
                    stroke-width="0.909203"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M114.29 122.462L111.413 81.0771"
                    stroke="#BCD75A"
                    stroke-width="0.903288"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M90.4505 95.6396L48.3004 98.0073"
                    stroke="#BCD75A"
                    stroke-width="0.905043"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M125.181 136.279L75.2719 140.204"
                    stroke="#BCD75A"
                    stroke-width="0.909031"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M31.1753 104.43C37.1846 104.43 42.799 106.376 47.567 109.782C55.7487 96.9057 68.9806 88.5703 83.877 88.5703C102.469 88.5703 118.41 101.544 125.265 120.031C129.469 115.685 134.999 113.057 141.093 113.057C154.184 113.057 164.792 125.252 164.792 140.301H128.848H117.394H62.3505H38.9056H0C0 120.485 13.9372 104.43 31.1753 104.43Z"
                    fill="#66983F"
                    fill-opacity="0.5"
                />
                <path
                    d="M92.115 141.274C92.115 141.274 85.09 98.9165 80.3503 88.5703"
                    stroke="#BCD75A"
                    stroke-width="1.233"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M90.3659 131.252L53.5198 102.127"
                    stroke="#BCD75A"
                    stroke-width="1.233"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M109.664 96.873L87.5728 116.56"
                    stroke="#BCD75A"
                    stroke-width="1.233"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M40.9087 141.274L13.0343 111.111"
                    stroke="#BCD75A"
                    stroke-width="1.233"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M35.4636 103.749L28.1847 127.523"
                    stroke="#BCD75A"
                    stroke-width="1.23243"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M136.325 140.269L150.995 114.42"
                    stroke="#BCD75A"
                    stroke-width="1.23243"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </div>

        <FeaturedGrid
            class="block pb-10 text-dark-green md:ml-[25%] md:max-w-[75%]"
            align="right"
            bg="light-green"
            color="dark-green"
            cta-color="dark-green"
            v-bind="attrs"
            :text="data.data.featuredContentText"
        />
    </div>
</template>
